import * as Yup from "yup";

const EMPTY_OPTION = { value: "", label: "" };

export type LTAFormValues = ReturnType<typeof emptyLTAFormValues>;

export function emptyLTAFormValues() {
  return {
    title: "",
    ltaNumber: "",
    startDate: "" as any,
    endDate: "" as any,
    ltaType: { ...EMPTY_OPTION },
    ltaService: { ...EMPTY_OPTION },
    ltaValue: "",
    regions: [] as any[],
    contractor: { ...EMPTY_OPTION },
    leadAgency: { ...EMPTY_OPTION },
    agencies: [] as any[],
    isUsableByOtherAgencies: false,
    additionalInformation: "",
    selectedFiles: [] as any[],
  };
}

/** Turns a saved LTA into the shape the form expects. */
export function ltaToFormValues(record: any) {
  return {
    title: record?.title || "",
    ltaNumber: record?.ltaNumber || "",
    startDate: (record?.startDate && new Date(record.startDate)) || "",
    endDate: (record?.endDate && new Date(record.endDate)) || "",
    ltaType: record?.ltaType?.id
      ? { value: record.ltaType.id, label: record.ltaType.name }
      : { ...EMPTY_OPTION },
    ltaService: record?.ltaService?.id
      ? { value: record.ltaService.id, label: record.ltaService.name }
      : { ...EMPTY_OPTION },
    ltaValue: record?.ltaValue ?? "",
    regions: (record?.location || []).map((region: any) => ({
      value: region?.id,
      label: region?.name,
    })),
    contractor: record?.contractor?.id
      ? {
          value: record.contractor.id,
          label: record.contractor.acronym
            ? `${record.contractor.contractorName} ( ${record.contractor.acronym} )`
            : record.contractor.contractorName,
        }
      : { ...EMPTY_OPTION },
    leadAgency: record?.leadAgency?.id
      ? { value: record.leadAgency.id, label: record.leadAgency.name }
      : { ...EMPTY_OPTION },
    agencies: (record?.agencies || []).map((agency: any) => ({
      value: agency?.id,
      label: agency?.name,
    })),
    isUsableByOtherAgencies: !!record?.isUsableByOtherAgencies,
    additionalInformation: record?.additionalInformation || "",
    // Already-uploaded documents carry an `id`; that is what marks them as
    // existing when the form is submitted.
    selectedFiles: (record?.ltaDocuments || []).map((file: any) => ({
      id: file.id,
      name: file.name,
      formattedSize: `${file.size} KB`,
    })),
  };
}

export const ltaValidationSchema = Yup.object({
  title: Yup.string().required("Please enter the LTA title"),
  ltaNumber: Yup.string().required("Please enter the LTA number"),
  startDate: Yup.mixed().nullable(),
  endDate: Yup.mixed()
    .nullable()
    .test(
      "after-start",
      "The end date must fall after the start date",
      function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return new Date(value as any) >= new Date(startDate);
      }
    ),
  ltaType: Yup.object().shape({ value: Yup.number().nullable() }),
  // Optional, matching the importer — legacy agreements are often migrated
  // before the service is known.
  ltaService: Yup.object().shape({ value: Yup.number().nullable() }),
  ltaValue: Yup.number()
    .transform((v, orig) => (orig === "" ? undefined : v))
    .typeError("LTA value must be a number")
    .min(0, "LTA value cannot be negative"),
  regions: Yup.array(),
  contractor: Yup.object().shape({
    value: Yup.number().required("Please select a contractor"),
  }),
  leadAgency: Yup.object().shape({ value: Yup.number().nullable() }),
  agencies: Yup.array(),
  isUsableByOtherAgencies: Yup.boolean(),
  additionalInformation: Yup.string().nullable(),
  selectedFiles: Yup.array(),
});

const toISODate = (value: any) =>
  (value && new Date(value).toLocaleDateString("en-CA")) || null;

/**
 * Builds the multipart body for create/update.
 *
 * The LTA endpoints accept documents alongside the record, so the JSON payload
 * goes in `data` and any newly dropped files in `files.ltaDocuments`. On
 * update, documents that already exist are echoed back in `ltaDocuments` —
 * omitting them would detach them from the record.
 */
export function buildLTAFormData(values: any, { isUpdate }: { isUpdate: boolean }) {
  const data: any = {
    title: values.title,
    ltaNumber: values.ltaNumber,
    startDate: toISODate(values.startDate),
    endDate: toISODate(values.endDate),
    ltaType: values.ltaType?.value || null,
    ltaService: values.ltaService?.value || null,
    ltaValue: values.ltaValue === "" || values.ltaValue === null ? null : Number(values.ltaValue),
    location: (values.regions || []).map((el: any) => el.value),
    contractor: values.contractor?.value || null,
    leadAgency: values.leadAgency?.value || null,
    agencies: (values.agencies || []).map((el: any) => el.value),
    isUsableByOtherAgencies: !!values.isUsableByOtherAgencies,
    additionalInformation: values.additionalInformation || null,
  };

  if (isUpdate) {
    data.ltaDocuments = (values.selectedFiles || [])
      .filter((file: any) => file?.id)
      .map((file: any) => file.id);
  }

  const formData = new FormData();
  (values.selectedFiles || []).forEach((file: any) => {
    if (!file?.id) {
      formData.append("files.ltaDocuments", file, file.name);
    }
  });
  formData.append("data", JSON.stringify(data));

  return formData;
}
